import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useLoadingOrdersSearchParams } from 'hooks/store/loading-orders';
import { useUnloadingOrdersSearchParams } from 'hooks/store/unloading-orders';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from 'components/ui/drawer';
import { Form, FormField } from 'components/ui/form';
import { MultiSelectGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';

import { formSchema, type FormSchema } from './formSchema';
import { columnOptions } from './data';

export type RefineReportParamsDrawerRef = {
  open: () => void;
};

export type Props = {
  onSubmit: (columns: string[]) => void;
  orderType: '1' | '2';
};

export const RefineReportParamsDrawer = React.forwardRef<
  RefineReportParamsDrawerRef,
  Props
>(({ onSubmit, orderType }, ref) => {
  const [open, setOpen] = React.useState(false);

  const loadingOrdersSearchParams = useLoadingOrdersSearchParams();
  const unloadingOrdersSearchParams = useUnloadingOrdersSearchParams();

  const { reportColumns, updateReportColumns } = React.useMemo(() => {
    return orderType === '1'
      ? loadingOrdersSearchParams
      : unloadingOrdersSearchParams
  }, [orderType, loadingOrdersSearchParams, unloadingOrdersSearchParams]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      columns: reportColumns,
    },
  });

  const handleFormSubmit = React.useCallback(
    (data: FormSchema) => {
      updateReportColumns(data.columns ?? []);
      onSubmit(data.columns ?? []);
    },
    [updateReportColumns, onSubmit]
  );

  React.useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
  }), []);

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle className="text-center">Refinar relatório</DrawerTitle>
        </DrawerHeader>
        <div className="max-w-[800px] w-full mx-auto">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleFormSubmit)}
              className="flex flex-col gap-4 pb-10"
            >
              <FormField
                control={form.control}
                name="columns"
                render={({ field }) => (
                  <MultiSelectGs
                    form={form}
                    fieldName={field.name}
                    fieldValue={field.value}
                    label={'Colunas'}
                    options={columnOptions}
                    maxCount={5}
                    modalPopover
                  />
                )}
              />
              <Button.Root type="submit" size="sm">
                Requisitar relatório
              </Button.Root>
            </form>
          </Form>
        </div>
      </DrawerContent>
    </Drawer>
  );
});
RefineReportParamsDrawer.displayName = 'RefineReportParamsDrawer';